import React, { useState, useEffect, useRef } from 'react';
import { useQuery } from 'react-query';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { clientService } from '../services/clientService';
import { Client } from '../types';
import LoadingSpinner from './LoadingSpinner';

interface ClientLookupProps {
  onSelect: (client: Client) => void;
  onCancel?: () => void;
  excludeClientIds?: number[]; // Clients to exclude from results
  placeholder?: string;
  minChars?: number;
}

const ClientLookup: React.FC<ClientLookupProps> = ({
  onSelect,
  onCancel,
  excludeClientIds = [],
  placeholder = 'Search for a client (minimum 3 characters)...',
  minChars = 3,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [showResults, setShowResults] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);

  // Debounced search query
  const [debouncedQuery, setDebouncedQuery] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 300); // 300ms debounce

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch clients when debounced query is long enough
  const { data: searchResults, isLoading, error } = useQuery({
    queryKey: ['clientLookup', debouncedQuery],
    queryFn: () => clientService.lookupClients(debouncedQuery),
    enabled: debouncedQuery.length >= minChars,
    staleTime: 0, // Always fetch fresh results
  });

  // Filter out excluded clients
  const filteredResults = (searchResults || []).filter(
    (client) => !excludeClientIds.includes(client.id)
  );

  // Close results when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        resultsRef.current &&
        !resultsRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    setShowResults(value.length >= minChars);
  };

  const handleSelectClient = (client: Client) => {
    onSelect(client);
    setSearchQuery('');
    setShowResults(false);
  };

  const handleClear = () => {
    setSearchQuery('');
    setShowResults(false);
    if (onCancel) {
      onCancel();
    }
  };

  return (
    <div className="relative w-full">
      <div className="relative">
        <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
        <input
          ref={inputRef}
          type="text"
          value={searchQuery}
          onChange={handleInputChange}
          onFocus={() => {
            if (searchQuery.length >= minChars) {
              setShowResults(true);
            }
          }}
          placeholder={placeholder}
          className="pl-10 pr-10 py-2 w-full border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
        />
        {searchQuery && (
          <button
            onClick={handleClear}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        )}
      </div>

      {/* Search Results Dropdown */}
      {showResults && searchQuery.length >= minChars && (
        <div
          ref={resultsRef}
          className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto"
        >
          {isLoading ? (
            <div className="p-4 text-center">
              <LoadingSpinner size="sm" />
            </div>
          ) : filteredResults.length > 0 ? (
            <ul className="py-1">
              {filteredResults.map((client) => (
                <li
                  key={client.id}
                  onClick={() => handleSelectClient(client)}
                  className="px-4 py-2 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                >
                  <div className="font-medium text-gray-900">{client.name}</div>
                  {client.email && (
                    <div className="text-sm text-gray-500">{client.email}</div>
                  )}
                  {client.industry && (
                    <div className="text-xs text-gray-400 mt-1">
                      Industry: {client.industry}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          ) : error ? (
            <div className="p-4 text-center text-red-500 text-sm">
              Failed to load clients. Please try again.
            </div>
          ) : (
            <div className="p-4 text-center text-gray-500 text-sm">
              No clients found matching &quot;{searchQuery}&quot;
            </div>
          )}
        </div>
      )}

      {searchQuery.length > 0 && searchQuery.length < minChars && (
        <p className="mt-1 text-xs text-gray-500">
          Enter at least {minChars} characters to search
        </p>
      )}
    </div>
  );
};

export default ClientLookup;

